<template>
  <div class="gig">
    <div
      v-if="showInvite"
      class="gig__invite"
    >
      <template v-if="showInviteFallback">
        <p>We couldn't do it for you, please share this link yourself:</p>
        <a :href="joinGigUrl">
          {{ joinGigUrl }}
        </a>
      </template>
      <button
        v-else
        @click="invite"
      >
        <template v-if="isInviteTextCopied">
          Link copied!
        </template>
        <template v-else>
          Invite others
        </template>
      </button>
    </div>
    <div v-else class="entities">
      <Entity
        v-for="entity in entitiesWithBalances"
        :key="entity.id"
        v-bind="entity"
      />
    </div>
    <h2>Transactions</h2>
    <button type="button" class="add-transaction" @click="addTransaction">＋</button>
    <div class="transaction-list">
      <div class="transaction" v-for="transaction in transactions"
        :key="transaction.id"
        @click="editTransaction(transaction.id)">
        <div class="transaction__purpose">{{ transaction.purpose }}</div>
        <div class="transaction__members">
          <Person
            v-for="person in transaction.sponsors.map(({ person }) => person)"
            :key="person.id"
            :size="AVATAR_SIZES.SMALL"
            v-bind="person"
          />
          <template v-if="transactionsShowMembers[transaction.id]">
            →
            <Person
              v-for="person in transaction.members"
              :key="person.id"
              :size="AVATAR_SIZES.SMALL"
              v-bind="person"
            />
          </template>
        </div>
        <div class="transaction__amount">{{ transactionSums[transaction.id] }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '../db.js';
import { AVATAR_SIZES } from '../constants.js'
import Entity from './Entity.vue';
import Person from './Person.vue';

let linkCopiedShowTimer;

export default {
  name: 'GigScreen',
  components: {
    Entity,
    Person,
  },
  props: {
      gigId: String,
  },
  data() {
    return {
      AVATAR_SIZES,
      entities: [],
      transactions: [],
      memberId: null,
      transactionSums: {},
      showInviteFallback: false,
      isInviteTextCopied: false,
    }
  },
  computed: {
    totalPeople() {
      return this.entities.reduce(
        (sum, entity) => sum + entity.people.length
      , 0);
    },
    joinGigUrl() {
      return 'https://accountit.app/join/' + this.gigId;
    },
    entitiesWithBalances() {
      const { entities, transactions } = this;
      const balances = {};
      for (const entity of entities) {
        balances[entity.id] = 0;
      }
      for (const transaction of transactions) {
        for (const sponsor of transaction.sponsors) {
          const eachMembersShare = sponsor.amount / transaction.members.length;
          for (const member of transaction.members) {
            const memberEntity = this.findPersonEntity(member);
            if (memberEntity) {
              balances[memberEntity.id] += eachMembersShare;
            }
          }
          const sponsorEntity = this.findPersonEntity(sponsor.person);
          if (sponsorEntity) {
            balances[sponsorEntity.id] -= sponsor.amount;
          }
        }
      }
      return entities.map(entity => {
        return {
          ...entity,
          id: entity.id,
          balance: balances[entity.id],
        };
      });
    },
    transactionsShowMembers() {
      return this.transactions.reduce((acc, transaction) => {
        acc[transaction.id] = transaction.members.length !== this.totalPeople;
        return acc;
      }, {});
    },
    showInvite() {
      return this.entitiesWithBalances.length === 1;
    },
  },
  methods: {
    addTransaction() {
      this.$router.push('/' + this.gigId + '/add-transaction');
    },
    editTransaction(id) {
      this.$router.push('/' + this.gigId + '/transaction/' + id);
    },
    findPersonEntity({ id }) {
      return this.entities.find(
        entity => entity.people.some(
          person => person.id === id
        )
      );
    },
    invite() {
      const url = this.joinGigUrl;
      const text = 'Join a gig on accountit to track our shared expenses.';
      if (navigator.share) {
        navigator.share({
          title: 'Accountit',
          text,
          url,
        });
      } else {
        try {
          navigator.clipboard.writeText(`${text}\n${url}`);
          this.isInviteTextCopied = true;
          clearTimeout(linkCopiedShowTimer);
          linkCopiedShowTimer = setTimeout(() => {
            this.isInviteTextCopied = false;
          }, 3000);
        } catch(e) {
          this.showInviteFallback = true;
        }
      }
    },
  },
  beforeMount() {
    //TODO refactor memberId access and storage
    const gigMap = JSON.parse(localStorage.gigMap);
    this.memberId = gigMap[this.gigId];
  },
  watch: {
    gigId: {
      immediate: true,
      handler(gigId) {
        const gig = db.collection('gigs').doc(gigId);
        this.$bind('entities', gig.collection('entities'), { maxRefDepth: 3 });
        this.$bind('transactions', gig.collection('transactions').orderBy('timestamp', 'desc')).then(
          refs => {
            refs.forEach(ref => {
              const sum = ref.sponsors.reduce(
                (acc, sponsor) => acc + sponsor.amount
              , 0);
              this.$set(this.transactionSums, ref.id, sum);
            });
          }
        );
      },
    },
  },
}
</script>

<style scoped>
.gig {
  padding-top: 1px;
  /* To prevent margin collapse */
}
.entities {
  display: flex;
  justify-content: center;
  overflow: hidden;
}
h2 {
  text-align: center;
  color: #6d547e;
}
.transaction {
  display:flex;
  align-items: center;
  padding: 0 20px;
}
.transaction__amount {
  font-size: 32px;
  margin-left: 1em;
}
.transaction__purpose {
  padding-top: 10px;
  font-size: 18px;
}
.transaction__members {
  margin: 0 auto 0 1em;
  margin-right: auto;
}
button {
  padding: 10px 15px;
  border-radius: 8px;
  border: none;
  background-color: #6d547e;
  font-size: 18px;
  color: white;
  outline: none;
}
button.add-transaction {
  display: block;
  margin: 20px auto;
  width: 80px;
  height: 80px;
  border-radius: 18px;
  border: none;
  background-color: #6d547e;
  font-size: 60px;
  color: white;
  outline: none;
  line-height: 1;
  padding: 0;
}
.entities {
  display: flex;
}
.gig__invite {
  display: block;
  margin: 2em auto;
  text-align: center;
}
</style>
